/**
 * Stale-chunk recovery shared by every React error boundary in the fleet.
 *
 * After a deploy purges the old hashed bundles, a lazy `import()` in a tab that
 * was opened before the deploy 404s ("Failed to fetch dynamically imported
 * module"). Soft-resetting the boundary just re-throws; only a hard reload
 * fetches fresh HTML with the new asset hashes. The helpers here decide whether
 * an error is that class of failure and reload at most ONCE per tab session, so
 * a genuinely missing asset can never put the page in a reload loop.
 *
 * Previously copy-pasted into ui-core's ErrorBoundary, the dashboard-shell
 * ShellErrorBoundary, the esl-patient / medspa-patient / dictate local
 * boundaries and ops `lib/chunk-reload.ts`. One implementation, one key.
 *
 * Pure TypeScript (no React import) so non-React code can use it as well.
 */

/** sessionStorage key holding the timestamp of the one allowed reload. */
export const CHUNK_RELOAD_KEY = "plerity:chunk-reload-attempted";

/** True when `error` is a stale-hash chunk/module load failure (Vite, webpack, CSS chunks). */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message || "";
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Loading chunk") ||
    msg.includes("Loading CSS chunk") ||
    // Safari/WebKit and Firefox phrase the same stale-module failure differently.
    msg.includes("Importing a module script failed") ||
    msg.includes("error loading dynamically imported module") ||
    error.name === "ChunkLoadError"
  );
}

export interface ChunkRecoveryOptions {
  /** Prefix for the console warning, e.g. `Shell:ops`. Default `ErrorBoundary`. */
  label?: string;
  /** Test seam: where the one-shot flag lives. Default `globalThis.sessionStorage`. */
  storage?: Pick<Storage, "getItem" | "setItem">;
  /** Test seam: how to reload. Default `window.location.reload()`. */
  reload?: () => void;
  /** Test seam: clock for the flag value. */
  now?: () => number;
}

function defaultStorage(): Pick<Storage, "getItem" | "setItem"> | null {
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    // Access to sessionStorage can itself throw (privacy modes, sandboxed iframes).
    return null;
  }
}

/**
 * If `error` is a stale-chunk failure and this tab has not reloaded for one yet,
 * flag the session and reload.
 *
 * @returns `true` when a reload was triggered (the caller should stop rendering
 *   and skip reporting; the page is about to be replaced), `false` when the
 *   error is not a chunk error OR the one allowed reload was already spent (the
 *   caller should render its normal error UI).
 */
export function recoverFromChunkError(error: unknown, options: ChunkRecoveryOptions = {}): boolean {
  if (!isChunkLoadError(error)) return false;
  const label = options.label ?? "ErrorBoundary";
  const storage = options.storage ?? defaultStorage();
  if (storage === null) return false;

  let alreadyReloaded: string | null = null;
  try {
    alreadyReloaded = storage.getItem(CHUNK_RELOAD_KEY);
  } catch {
    return false;
  }
  if (alreadyReloaded) {
    console.error(`[${label}] Chunk load failed even after reload:`, (error as Error).message);
    return false;
  }

  try {
    storage.setItem(CHUNK_RELOAD_KEY, String((options.now ?? Date.now)()));
  } catch {
    // Cannot persist the guard, so reloading could loop forever. Do not reload.
    return false;
  }
  console.warn(`[${label}] Stale chunk detected — reloading to fetch fresh assets.`);
  (options.reload ?? (() => window.location.reload()))();
  return true;
}

/**
 * Clear the one-shot flag. Call after the app has mounted successfully so the
 * next deploy can trigger a fresh auto-reload.
 */
export function clearChunkReloadFlag(storage?: Pick<Storage, "removeItem">): void {
  try {
    (storage ?? sessionStorage).removeItem(CHUNK_RELOAD_KEY);
  } catch {
    // sessionStorage may be unavailable; nothing to clear.
  }
}
